import { useAuth } from "@/context/auth-provider";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { User, MapPin, Users, Settings } from "lucide-react";
import { ProfileStatsCards } from "@/components/profile/ProfileStatsCards";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { BasicInfoTab } from "@/components/profile/tabs/BasicInfoTab";
import { AddressesTab } from "@/components/profile/tabs/AddressesTab";
import { ContactsTab } from "@/components/profile/tabs/ContactsTab";
import { SettingsTab } from "@/components/profile/tabs/SettingsTab";
import { useProfileData } from "@/hooks/useProfileData";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";

const NewProfile = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { data: profileData, isLoading } = useProfileData();

  // Handle authentication redirect
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
    }
  }, [user, authLoading, navigate]);

  if (authLoading || !user) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <Card className="p-8">
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <Skeleton className="h-20 w-20 rounded-full mx-auto" />
              <Skeleton className="h-6 w-32 mx-auto" />
              <Skeleton className="h-4 w-48 mx-auto" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="p-6">
                  <Skeleton className="h-16 w-full" />
                </Card>
              ))}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Profile Header */}
        <ProfileHeader 
          profile={profileData?.profile}
          roles={profileData?.roles}
          isLoading={isLoading}
        />

        {/* Stats Cards */}
        <ProfileStatsCards 
          stats={profileData?.stats}
          memberSince={profileData?.profile?.created_at}
          isLoading={isLoading}
        />

        {/* Main Content Tabs */}
        <Tabs defaultValue="basic" className="space-y-6">
          <div className="border-b border-border">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="basic" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">Basic Info</span>
              </TabsTrigger>
              <TabsTrigger value="addresses" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                <span className="hidden sm:inline">Addresses</span>
              </TabsTrigger>
              <TabsTrigger value="contacts" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span className="hidden sm:inline">Contacts</span>
              </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Settings</span>
            </TabsTrigger>
          </TabsList>
          </div>

          <TabsContent value="basic">
            <BasicInfoTab 
              profile={profileData?.profile}
              isLoading={isLoading}
            />
          </TabsContent>

          <TabsContent value="addresses">
            <AddressesTab />
          </TabsContent>

          <TabsContent value="contacts">
            <ContactsTab />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsTab />
          </TabsContent>
        </Tabs>
      </div>
    </ErrorBoundary>
  );
};

export default NewProfile;